/**
 * Core utility methods for sessionStorage operations
 * Provides type-safe, error-handled access to browser sessionStorage
 */

/**
 * Check if sessionStorage is available in the current environment
 */
const isAvailable = (): boolean => {
  try {
    const test = '__sessionStorage_test__';
    sessionStorage.setItem(test, test);
    sessionStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get an item from sessionStorage
 * @param key - The key to retrieve
 * @returns The parsed value or null if not found or on error
 */
export const getItem = <T = unknown>(key: string): T | null => {
  if (!isAvailable()) {
    console.warn('sessionStorage is not available');
    return null;
  }

  try {
    const item = sessionStorage.getItem(key);
    if (item === null) {
      return null;
    }
    return JSON.parse(item) as T;
  } catch (error) {
    console.error(`Error getting item from sessionStorage with key "${key}":`, error);
    return null;
  }
};

/**
 * Set an item in sessionStorage
 * @param key - The key to store the value under
 * @param value - The value to store (will be JSON stringified)
 * @returns true if successful, false otherwise
 */
export const setItem = <T = unknown>(key: string, value: T): boolean => {
  if (!isAvailable()) {
    console.warn('sessionStorage is not available');
    return false;
  }

  try {
    const serialized = JSON.stringify(value);
    sessionStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    console.error(`Error setting item in sessionStorage with key "${key}":`, error);
    return false;
  }
};

/**
 * Remove an item from sessionStorage
 * @param key - The key to remove
 * @returns true if successful, false otherwise
 */
export const removeItem = (key: string): boolean => {
  if (!isAvailable()) {
    console.warn('sessionStorage is not available');
    return false;
  }

  try {
    sessionStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing item from sessionStorage with key "${key}":`, error);
    return false;
  }
};

/**
 * Clear all items from sessionStorage
 * @returns true if successful, false otherwise
 */
export const clear = (): boolean => {
  if (!isAvailable()) {
    console.warn('sessionStorage is not available');
    return false;
  }

  try {
    sessionStorage.clear();
    return true;
  } catch (error) {
    console.error('Error clearing sessionStorage:', error);
    return false;
  }
};

/**
 * Check if a key exists in sessionStorage
 * @param key - The key to check
 * @returns true if the key exists, false otherwise
 */
export const hasItem = (key: string): boolean => {
  if (!isAvailable()) {
    return false;
  }

  try {
    return sessionStorage.getItem(key) !== null;
  } catch (error) {
    console.error(`Error checking item in sessionStorage with key "${key}":`, error);
    return false;
  }
};

/**
 * Get all keys from sessionStorage
 * @returns Array of all keys, or empty array on error
 */
export const getAllKeys = (): string[] => {
  if (!isAvailable()) {
    return [];
  }

  try {
    return Object.keys(sessionStorage);
  } catch (error) {
    console.error('Error getting all keys from sessionStorage:', error);
    return [];
  }
};

/**
 * Get the number of items in sessionStorage
 * @returns The number of items, or 0 on error
 */
export const getLength = (): number => {
  if (!isAvailable()) {
    return 0;
  }

  try {
    return sessionStorage.length;
  } catch (error) {
    console.error('Error getting sessionStorage length:', error);
    return 0;
  }
};

/**
 * sessionStorage utility object with all methods
 */
export const sessionStorageUtil = {
  getItem,
  setItem,
  removeItem,
  clear,
  hasItem,
  getAllKeys,
  getLength,
  isAvailable,
};

export default sessionStorageUtil;

